import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, formatPrice } from '../lib/api';
import { toEmbedUrl } from '../lib/embed';
import { ArrowLeft, Check, ZoomIn } from 'lucide-react';
import Lightbox from '../components/Lightbox';
import MachineInquiryCard from '../components/MachineInquiryCard';
import BuyBox from '../components/BuyBox';
import { isBuyNow, stockStatus } from '../lib/shop';
import RelatedContent from '../components/RelatedContent';
import CTASection, { buildContactHref } from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const trackClick = (name, params = {}) => {
    if (typeof window !== 'undefined' && window.trackConversion) window.trackConversion(name, params);
};

const MachineDetail = () => {
    const { key } = useParams();
    const [m, setM] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [activeImg, setActiveImg] = useState('');
    const [lightboxIdx, setLightboxIdx] = useState(-1);
    const [related, setRelated] = useState([]);
    const [relatedProjects, setRelatedProjects] = useState([]);

    useEffect(() => {
        api.get(`/machines/${key}`)
            .then((r) => {
                setM(r.data);
                setActiveImg(r.data.image_url);
            })
            .catch(() => setNotFound(true));
    }, [key]);

    // Fetch projects that share this machine's category — "seen in the field"
    useEffect(() => {
        if (!m) return;
        api.get('/projects').then((r) => {
            const all = r.data || [];
            const sameCat = all.filter((p) => p.category === m.category);
            const pick = (sameCat.length ? sameCat : all).slice(0, 3);
            setRelatedProjects(pick);
        }).catch(() => setRelatedProjects([]));
    }, [m]);

    // Fetch related machines (same category, excluding current)
    useEffect(() => {
        if (!m) return;
        api.get('/machines', { params: { category: m.category } })
            .then((r) => {
                const others = (r.data || []).filter((x) => x.id !== m.id).slice(0, 3);
                setRelated(others);
            })
            .catch(() => setRelated([]));
    }, [m]);

    // Inject BreadcrumbList JSON-LD
    useEffect(() => {
        if (!m) return undefined;
        const scriptId = 'machine-breadcrumb-jsonld';
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const data = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
                { '@type': 'ListItem', position: 2, name: 'Machines', item: `${origin}/machines` },
                { '@type': 'ListItem', position: 3, name: m.name, item: `${origin}/machines/${m.slug || m.id}` },
            ],
        };
        let el = document.getElementById(scriptId);
        if (!el) {
            el = document.createElement('script');
            el.id = scriptId;
            el.type = 'application/ld+json';
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data);
        return () => { const node = document.getElementById(scriptId); if (node) node.remove(); };
    }, [m]);

    useSEO(m ? {
        title: `${m.name} — ${m.category.replace(/-/g, ' ')} equipment`,
        description: (m.short_description || m.description || `${m.name} available from Dry Ice Bulls. TimePayment financing options.`).slice(0, 160),
        image: m.image_url || '',
        type: 'product',
    } : {});

    // Inject Product JSON-LD schema for Google Shopping / rich results
    useEffect(() => {
        if (!m) return undefined;
        const scriptId = 'machine-product-jsonld';
        const absUrl = (u) => {
            if (!u) return '';
            if (/^https?:\/\//i.test(u)) return u;
            if (typeof window !== 'undefined') return `${window.location.origin}${u.startsWith('/') ? '' : '/'}${u}`;
            return u;
        };
        const images = [m.image_url, ...(m.gallery || [])].filter(Boolean).map(absUrl);
        const offer = m.contact_for_price
            ? {
                '@type': 'Offer',
                availability: 'https://schema.org/InStock',
                priceCurrency: m.currency || 'USD',
                priceSpecification: { '@type': 'PriceSpecification', description: 'Contact for pricing' },
                seller: { '@type': 'Organization', name: 'Dry Ice Bulls' },
                url: typeof window !== 'undefined' ? window.location.href : '',
            }
            : {
                '@type': 'Offer',
                price: m.price,
                priceCurrency: m.currency || 'USD',
                availability: stockStatus(m) === 'in_stock' ? 'https://schema.org/InStock' : 'https://schema.org/BackOrder',
                seller: { '@type': 'Organization', name: 'Dry Ice Bulls' },
                url: typeof window !== 'undefined' ? window.location.href : '',
            };

        const data = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: m.name,
            description: (m.description || m.short_description || m.name).slice(0, 5000),
            image: images.length > 0 ? images : undefined,
            sku: m.slug || m.id,
            category: m.category,
            brand: { '@type': 'Brand', name: 'Dry Ice Bulls' },
            offers: offer,
        };

        let el = document.getElementById(scriptId);
        if (!el) {
            el = document.createElement('script');
            el.id = scriptId;
            el.type = 'application/ld+json';
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data);

        return () => {
            const node = document.getElementById(scriptId);
            if (node) node.remove();
        };
    }, [m]);

    if (notFound) {
        return (
            <div className="container-page pt-32 pb-24" data-testid="machine-not-found">
                <h1 className="font-display font-black uppercase text-4xl text-neutral-900">Machine not found</h1>
                <Link to="/machines" className="mt-6 inline-flex items-center gap-2 text-brand-primary label-caps">
                    <ArrowLeft className="h-4 w-4" /> Back to catalog
                </Link>
            </div>
        );
    }

    if (!m) return <div className="container-page pt-32 pb-24 text-neutral-500">Loading…</div>;

    const gallery = [m.image_url, ...(m.gallery || [])].filter(Boolean);
    const activeIdx = Math.max(0, gallery.indexOf(activeImg));

    return (
        <div data-testid="machine-detail-page">
            <div className="container-page pt-28 pb-16">
                <Link to="/machines" className="inline-flex items-center gap-2 label-caps text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="machine-back-link">
                    <ArrowLeft className="h-4 w-4" /> Back to catalog
                </Link>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    <div className="lg:col-span-7">
                        <button
                            type="button"
                            onClick={() => gallery.length > 0 && setLightboxIdx(activeIdx >= 0 ? activeIdx : 0)}
                            className="group relative w-full aspect-square lg:aspect-[4/3] border border-neutral-200 bg-neutral-50 overflow-hidden flex items-center justify-center cursor-zoom-in"
                            data-testid="machine-hero-zoom"
                            aria-label="Zoom image"
                        >
                            {activeImg ? <img src={activeImg} alt={m.name} fetchPriority="high" decoding="async" className="w-full h-full object-contain" /> : null}
                            <span className="absolute top-3 right-3 md:top-4 md:right-4 flex items-center gap-1.5 label-caps text-white bg-black/60 backdrop-blur-sm border border-white/20 px-2.5 py-1.5 text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                <ZoomIn className="h-3.5 w-3.5" /> Click to zoom
                            </span>
                        </button>
                        {gallery.length > 1 && (
                            <div className="mt-4 grid grid-cols-5 md:grid-cols-6 gap-3">
                                {gallery.map((g, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveImg(g)}
                                        className={`aspect-square border transition-colors overflow-hidden bg-neutral-50 ${activeImg === g ? 'border-brand-primary' : 'border-neutral-200 hover:border-neutral-400'}`}
                                        data-testid={`gallery-thumb-${i}`}
                                    >
                                        <img src={g} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {m.video_url && (
                            <div className="mt-6 aspect-video border border-neutral-200 bg-black">
                                <iframe
                                    src={toEmbedUrl(m.video_url)}
                                    className="w-full h-full"
                                    title={m.name}
                                    loading="lazy"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        )}

                        <div className="mt-8">
                            <div className="label-caps text-brand-primary mb-3 text-xs">{m.category.replace(/-/g, ' ')}</div>
                            <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl text-neutral-900">{m.name}</h1>
                            <p className="mt-4 text-neutral-600 leading-relaxed text-base md:text-lg">{m.short_description}</p>
                        </div>

                        <BuyBox m={m} />
                        {m.rentable && (
                            <Link to={`/rental?machine=${m.id}`} className="mt-3 flex items-center justify-between gap-3 border border-brand-primary/40 bg-brand-primary/5 px-4 py-3 hover:bg-brand-primary/10 transition-colors" data-testid="machine-rent-badge">
                                <span className="text-sm text-neutral-800"><b>Also available to rent</b>{m.rental_daily ? ` from ${formatPrice(m.rental_daily)}/day` : ''}</span>
                                <span className="label-caps text-xs text-brand-primary">Rent it →</span>
                            </Link>
                        )}

                        {m.features && m.features.length > 0 && (
                            <div className="mt-10">
                                <div className="label-caps text-neutral-500 mb-3">Features</div>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {m.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-3 text-neutral-800">
                                            <Check className="h-5 w-5 text-brand-primary flex-shrink-0 mt-0.5" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {m.specs && m.specs.length > 0 && (
                            <div className="mt-10">
                                <div className="label-caps text-neutral-500 mb-3">Specifications</div>
                                <dl className="border border-neutral-200 divide-y divide-neutral-200">
                                    {m.specs.map((s, i) => (
                                        <div key={i} className="grid grid-cols-2 px-4 py-3 text-sm">
                                            <dt className="text-neutral-500">{s.label}</dt>
                                            <dd className="text-neutral-900 font-mono">{s.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {m.description && (
                            <section className="mt-14 max-w-3xl">
                                <div className="label-caps text-brand-primary mb-4">Description</div>
                                <div className="text-neutral-700 leading-relaxed whitespace-pre-line">{m.description}</div>
                            </section>
                        )}
                    </div>

                    {/* Sticky sidebar — inquiry form only shows when the machine has no visible price
                        (i.e. "Contact for Pricing"). For priced machines the primary Buy / Quote CTAs
                        at the top of the page are enough; we don't repeat a full form here. */}
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28">
                            {!isBuyNow(m) && (
                                <MachineInquiryCard machineName={m.name} machineCategory={m.category} />
                            )}

                            {/* Quick-actions panel */}
                            <div className={`${!isBuyNow(m) ? 'mt-4' : ''} border border-neutral-200 bg-neutral-50 p-5`} data-testid="machine-quick-actions">
                                <div className="label-caps text-brand-primary text-xs mb-3">/ Other options</div>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <Link
                                        to={buildContactHref({ subject: `Buy used: ${m.name}`, machine: m.name, interest: 'quote' })}
                                        onClick={() => trackClick('equipment_quote_click', { location: 'machine_actions', machine: m.name, sub: 'buy_used' })}
                                        className="border border-neutral-300 bg-white px-3 py-2.5 hover:border-brand-primary hover:text-brand-primary transition-colors"
                                    >
                                        → Buy used
                                    </Link>
                                    <Link
                                        to={m.rentable ? `/rental?machine=${m.id}` : '/rental'}
                                        onClick={() => trackClick('quote_click', { location: 'machine_actions', machine: m.name, sub: 'rental' })}
                                        className="border border-neutral-300 bg-white px-3 py-2.5 hover:border-brand-primary hover:text-brand-primary transition-colors"
                                    >
                                        → Rent first
                                    </Link>
                                    <Link
                                        to="/training"
                                        onClick={() => trackClick('training_click', { location: 'machine_actions', machine: m.name })}
                                        className="border border-neutral-300 bg-white px-3 py-2.5 hover:border-brand-primary hover:text-brand-primary transition-colors"
                                    >
                                        → Get operator training
                                    </Link>
                                    <Link
                                        to={buildContactHref({ subject: `Consultation: ${m.name}`, machine: m.name, interest: 'quote' })}
                                        onClick={() => trackClick('specialist_click', { location: 'machine_actions', machine: m.name })}
                                        className="border border-neutral-300 bg-white px-3 py-2.5 hover:border-brand-primary hover:text-brand-primary transition-colors"
                                    >
                                        → Consult a specialist
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* You might also like — related machines in same category */}
                {related.length > 0 && (
                    <section className="mt-24 border-t border-neutral-200 pt-14" data-testid="machine-related">
                        <div className="flex items-baseline justify-between mb-8 gap-6">
                            <div>
                                <div className="label-caps text-brand-primary mb-3 text-xs">/ You might also like</div>
                                <h2 className="font-display font-black uppercase text-3xl md:text-4xl leading-tight text-neutral-900">
                                    More {m.category.replace(/-/g, ' ')} equipment
                                </h2>
                            </div>
                            <Link
                                to="/machines"
                                className="hidden md:inline-flex items-center gap-1.5 label-caps text-neutral-500 hover:text-brand-primary transition-colors text-xs"
                                data-testid="machine-related-see-all"
                            >
                                See all machines <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {related.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/machines/${r.slug || r.id}`}
                                    className="group border border-neutral-200 hover:border-brand-primary bg-white flex flex-col transition-colors"
                                    data-testid={`related-machine-${r.slug || r.id}`}
                                >
                                    <div className="aspect-square bg-neutral-50 overflow-hidden flex items-center justify-center">
                                        {r.image_url ? (
                                            <img src={r.image_url} alt={r.name} loading="lazy" decoding="async" className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                                        ) : null}
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        <div className="label-caps text-neutral-500 mb-2 text-xs">{r.category.replace(/-/g, ' ')}</div>
                                        <h3 className="font-display font-black uppercase text-lg leading-tight text-neutral-900 group-hover:text-brand-primary transition-colors">
                                            {r.name}
                                        </h3>
                                        <p className="mt-2 text-sm text-neutral-600 leading-relaxed line-clamp-2 flex-1">
                                            {r.short_description}
                                        </p>
                                        <div className="mt-4">
                                            {r.contact_for_price ? (
                                                <span className="label-caps text-neutral-500 border border-neutral-300 px-2 py-1 inline-block text-xs">
                                                    Contact for Pricing
                                                </span>
                                            ) : (
                                                <div className="font-display font-black text-xl text-brand-primary">
                                                    {formatPrice(r.price)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {/* Related projects — "seen in the field" */}
            {relatedProjects.length > 0 && (
                <RelatedContent
                    eyebrow="/ Seen in the field"
                    title={`Projects that used ${m.category.replace(/-/g, ' ')} equipment`}
                    items={relatedProjects}
                    kind="project"
                    testid="machine-related-projects"
                />
            )}

            {/* Bottom CTA — full-bleed conversion block */}
            <CTASection
                variant="machine"
                context={{ machine: m.name, trackingLocation: 'machine_bottom', priced: isBuyNow(m) }}
                heading={`Ready to move on ${m.name}?`}
                subheading={isBuyNow(m) ? 'Order online today, finance it, or talk to an equipment specialist about setup and training.' : 'Get pricing, financing options, and lead-times from an equipment specialist within one business day.'}
                testid="machine-cta-bottom"
            />

            {lightboxIdx >= 0 && gallery.length > 0 && (
                <Lightbox
                    images={gallery}
                    index={lightboxIdx}
                    onClose={() => setLightboxIdx(-1)}
                    onIndex={(i) => { setLightboxIdx(i); setActiveImg(gallery[i]); }}
                />
            )}
        </div>
    );
};

export default MachineDetail;
